/**
 * Finances → Progress Billing.
 *
 * Work in progress by project on a cost-to-cost percent-complete basis:
 * earned revenue vs. billed to date, over/(under) billings, a completed list,
 * and CSV / PDF reports for the CPA and bookkeeper. Numbers match the
 * `progressBillingWip` finance-agent action.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Download,
  FileSpreadsheet,
  FileText,
  Info,
  Loader2,
} from 'lucide-react';
import { useProgressBillingData } from '../../hooks/useProgressBillingData';
import {
  PROGRESS_BILLING_COLLECTED_MARK,
  PROGRESS_BILLING_COLLECTED_NOTE,
  PROGRESS_BILLING_REPORT_LABELS,
  PROGRESS_INSTALLMENT_LABELS,
  partitionSmallContracts,
  rollupSmallContracts,
  type ProgressBillingProject,
  type ProgressBillingReportKind,
  type ProgressBillingSmallRollup,
} from '../../utils/progressBilling';
import { exportProgressBillingCsv, exportProgressBillingPdf } from '../../utils/progressBillingReport';
import '../../pages/ProjectPLPage.css';
import './ProgressBillingSection.css';

const REPORT_KINDS: ProgressBillingReportKind[] = ['wip_schedule', 'completed_projects', 'billing_summary'];

const REPORT_HINTS: Record<ProgressBillingReportKind, string> = {
  wip_schedule: 'Open jobs by contract size. Jobs under $10,000 are one combined line.',
  completed_projects: 'Finalized jobs by contract size. Jobs under $10,000 are one combined line.',
  billing_summary: 'Every job: billed, collected, open A/R, remaining to bill, next installment.',
};

function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export const progressBillingTodayInput = (): string => ymd(new Date());

function parseYmdLocal(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
  return Number.isNaN(d.getTime()) ? null : d;
}

const fmtMoney = (value: number): string =>
  `$${Math.round(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const fmtMoneyShort = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtPct = (value: number | null): string => (value == null ? '—' : `${Math.round(value * 100)}%`);

/** Positive = over-billed (shown as a liability), negative = under-billed (asset). */
const fmtOverUnder = (value: number): string => {
  if (Math.abs(value) < 1) return '$0';
  return value > 0 ? fmtMoney(value) : `(${fmtMoney(Math.abs(value))})`;
};

const fmtDate = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const shortLabel = (label: string, max = 28): string => (label.length > max ? `${label.slice(0, max - 1)}…` : label);

interface ChartDatum {
  dealId: string;
  name: string;
  fullName: string;
  earned: number;
  billed: number;
  netOverUnder: number;
  percentComplete: number | null;
}

const METHOD_TIP_ID = 'pbill-method-tip';

const WipRollupRow: React.FC<{ rollup: ProgressBillingSmallRollup }> = ({ rollup }) => (
  <div className="fin-table-row pbill-grid pbill-grid-wip pbill-rollup-row">
    <span className="fin-cell">
      <span className="fin-cell-primary">{rollup.label}</span>
      <div className="fin-cell-sub">{rollup.clientName}</div>
    </span>
    <span className="fin-cell fin-cell-money">{fmtMoney(rollup.contractValue)}</span>
    <span className="fin-cell fin-cell-money">{rollup.estimatedCost > 0 ? fmtMoney(rollup.estimatedCost) : <span className="fin-cell-muted">—</span>}</span>
    <span className="fin-cell fin-cell-money">{fmtMoney(rollup.costToDate)}</span>
    <span className="fin-cell fin-cell-money">
      <span className="pbill-pct">
        <span className="pbill-pct-bar"><span style={{ width: `${Math.round((rollup.percentComplete ?? 0) * 100)}%` }} /></span>
        {fmtPct(rollup.percentComplete)}
      </span>
    </span>
    <span className="fin-cell fin-cell-money">{rollup.percentComplete == null ? '—' : fmtMoney(rollup.earnedRevenue)}</span>
    <span className="fin-cell fin-cell-money">{fmtMoney(rollup.billedToDate)}</span>
    <span className={`fin-cell fin-cell-money ${rollup.netOverUnder > 0 ? 'is-over' : rollup.netOverUnder < 0 ? 'is-under' : ''}`}>
      {rollup.percentComplete == null ? '—' : fmtOverUnder(rollup.netOverUnder)}
    </span>
    <span className="fin-cell"><span className="fin-cell-muted">—</span></span>
  </div>
);

const CompletedRollupRow: React.FC<{ rollup: ProgressBillingSmallRollup }> = ({ rollup }) => {
  const gap = rollup.billedToDate - rollup.contractValue;
  return (
    <div className="fin-table-row pbill-grid pbill-grid-done pbill-rollup-row">
      <span className="fin-cell">
        <span className="fin-cell-primary">{rollup.label}</span>
        <div className="fin-cell-sub">{rollup.clientName}</div>
      </span>
      <span className="fin-cell">—</span>
      <span className="fin-cell fin-cell-money">{fmtMoney(rollup.contractValue)}</span>
      <span className="fin-cell fin-cell-money">{fmtMoney(rollup.costToDate)}</span>
      <span className={`fin-cell fin-cell-money ${rollup.estimatedGrossProfit < 0 ? 'is-over' : ''}`}>{fmtPct(rollup.estimatedGrossProfitPct)}</span>
      <span className="fin-cell fin-cell-money">{fmtMoney(rollup.billedToDate)}</span>
      <span className="fin-cell fin-cell-money">
        {Math.abs(rollup.contractValue) < 1 && Math.abs(rollup.billedToDate) < 1 ? (
          <span className="fin-cell-muted">—</span>
        ) : Math.abs(gap) < 1 ? (
          <span className="fin-pill fin-pill-status" data-status="filled">Fully billed</span>
        ) : (
          fmtOverUnder(gap)
        )}
      </span>
    </div>
  );
};

export const ProgressBillingMethodInfo: React.FC = () => (
  <span className="pbill-info">
    <button
      type="button"
      className="pbill-info-btn"
      aria-label="How progress billing is calculated"
      aria-describedby={METHOD_TIP_ID}
    >
      <Info size={13} strokeWidth={2.4} />
    </button>
    <span id={METHOD_TIP_ID} className="pbill-info-tip" role="tooltip">
      Cost-to-cost percent complete is actual job cost less allocated overhead, divided by the
      proposed budget. Only under-contract jobs are included. Finalized jobs count as 100%.
      Earned revenue = contract × that percent. Billed above earned is unearned revenue; billed
      below is under-billed. Pre-CRM jobs without a referenced proposal show collected cash
      ({PROGRESS_BILLING_COLLECTED_MARK}) instead of a $0 contract.
    </span>
  </span>
);

export const ProgressBillingAsOfPicker: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => (
  <label className="pbill-asof">
    <span>As of</span>
    <input type="date" value={value} max={ymd(new Date())} onChange={e => onChange(e.target.value)} />
  </label>
);

const toChartDatum = (p: ProgressBillingProject): ChartDatum => ({
  dealId: p.dealId,
  name: shortLabel(p.contractValueSource === 'collected' ? `${p.label} ${PROGRESS_BILLING_COLLECTED_MARK}` : p.label),
  fullName: p.label,
  earned: p.earnedRevenue,
  billed: p.billedToDate,
  netOverUnder: p.netOverUnder,
  percentComplete: p.percentComplete,
});

const ContractCell: React.FC<{ p: ProgressBillingProject }> = ({ p }) => (
  <span
    className="fin-cell fin-cell-money"
    title={p.contractValueSource === 'collected' ? PROGRESS_BILLING_COLLECTED_NOTE : undefined}
  >
    {fmtMoney(p.contractValue)}
    {p.contractValueSource === 'collected' && <span className="pbill-collected-mark">{PROGRESS_BILLING_COLLECTED_MARK}</span>}
  </span>
);

const EarnedBilledTooltip: React.FC<{ active?: boolean; payload?: Array<{ payload: ChartDatum }> }> = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <div className="pbill-tooltip">
      <div className="pbill-tooltip-title">{d.fullName}</div>
      <div className="pbill-tooltip-row"><span>Earned revenue</span><strong>{fmtMoney(d.earned)}</strong></div>
      <div className="pbill-tooltip-row"><span>Billed to date</span><strong>{fmtMoney(d.billed)}</strong></div>
      <div className="pbill-tooltip-row">
        <span>{d.netOverUnder < 0 ? 'Under-billed' : 'Unearned Revenue'}</span>
        <strong className={d.netOverUnder < 0 ? 'is-under' : ''}>
          {fmtMoney(Math.abs(d.netOverUnder))}
        </strong>
      </div>
      <div className="pbill-tooltip-row"><span>% complete</span><strong>{fmtPct(d.percentComplete)}</strong></div>
    </div>
  );
};

interface ProgressBillingSectionProps {
  asOfInput?: string;
}

const ProgressBillingSection: React.FC<ProgressBillingSectionProps> = ({ asOfInput }) => {
  const navigate = useNavigate();
  const resolvedAsOfInput = asOfInput || progressBillingTodayInput();
  const asOf = useMemo(() => parseYmdLocal(resolvedAsOfInput) ?? new Date(), [resolvedAsOfInput]);
  const data = useProgressBillingData(asOf);
  const [exporting, setExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  // Under-contract WIP only — preconstruction and estimating stay on Project P&L.
  const chartData = useMemo(
    () => data.wip.filter(p => p.percentComplete != null).map(toChartDatum),
    [data.wip],
  );
  const wipDisplay = useMemo(() => partitionSmallContracts(data.wip), [data.wip]);
  const wipSmall = useMemo(() => rollupSmallContracts(wipDisplay.small), [wipDisplay.small]);
  const completedDisplay = useMemo(() => partitionSmallContracts(data.completed), [data.completed]);
  const completedSmall = useMemo(() => rollupSmallContracts(completedDisplay.small), [completedDisplay.small]);
  const chartHeightFor = (count: number) => Math.max(120, Math.min(640, count * 36 + 60));
  const hasCollected = useMemo(() => data.projects.some(p => p.contractValueSource === 'collected'), [data.projects]);

  const runExport = useCallback(
    async (kind: ProgressBillingReportKind, format: 'csv' | 'pdf') => {
      const key = `${kind}:${format}`;
      setExporting(key);
      setExportError(null);
      try {
        if (format === 'csv') await exportProgressBillingCsv(kind, data.projects, asOf);
        else await exportProgressBillingPdf(kind, data.projects, asOf);
      } catch (err) {
        console.error('Progress billing export failed:', err);
        setExportError(err instanceof Error ? err.message : 'Export failed');
      } finally {
        setExporting(null);
      }
    },
    [asOf, data.projects],
  );

  const openProjectPL = useCallback(
    (p: ProgressBillingProject) => {
      navigate(`/finances/project-pl?deal=${encodeURIComponent(p.dealId)}`);
    },
    [navigate],
  );

  if (!data.canAccess) {
    return (
      <div className="ppl-gate">
        <h2>Project P&amp;L access required</h2>
        <p>
          Progress Billing reads the same job costs as Project P&amp;L, so it needs the same access: admins, or
          employees with the <strong>Project Manager</strong> or <strong>Executive Assistant</strong> designation.
        </p>
      </div>
    );
  }

  const { totals } = data;
  const netClass = totals.netOverUnder > 0 ? 'is-over' : totals.netOverUnder < 0 ? 'is-under' : '';

  return (
    <div className="pbill">
      {!data.ready && (
        <div className="pbill-loading"><Loader2 size={14} className="pbill-spin" /> Loading job costs and invoices…</div>
      )}

      {data.ready && !data.hasBudgets ? (
        <div className="fin-table-wrap">
          <div className="fin-table-empty">
            No proposal budgets yet.
            <div className="fin-table-empty-sub">
              Percent complete needs a budget from the signed proposal. Once jobs have proposal budgets and QuickBooks
              costs, they show here as work in progress.
            </div>
          </div>
        </div>
      ) : (
        <>
          <section className="pbill-card">
            <header className="pbill-card-head">
              <h3>Earned vs. billed — work in progress</h3>
              <span className="pbill-card-hint">Under-contract jobs · hover a bar for unearned revenue.</span>
            </header>
            {chartData.length === 0 ? (
              <div className="fin-table-empty">
                No open jobs with a budget.
                <div className="fin-table-empty-sub">Jobs without a proposal budget are listed below without a percent.</div>
              </div>
            ) : (
              <div className="pbill-chart" style={{ height: chartHeightFor(chartData.length) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }} barCategoryGap={10}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                    <XAxis type="number" tickFormatter={fmtMoneyShort} tick={{ fontSize: 11, fill: 'var(--color-text-secondary)' }} />
                    <YAxis type="category" dataKey="name" width={190} tick={{ fontSize: 11, fill: '#3F3A34' }} interval={0} />
                    <Tooltip content={<EarnedBilledTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="earned" name="Earned revenue" fill="#2C3E2F" radius={[0, 3, 3, 0]} />
                    <Bar dataKey="billed" name="Billed to date" fill="#B08D57" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          <section className="fin-table-wrap pbill-table-wrap">
            <div className="pbill-table-title">
              <h3>Work in progress</h3>
              <span className="pbill-card-hint">Largest contract first · jobs under $10,000 are combined.</span>
            </div>
            <div className="fin-table-header pbill-grid pbill-grid-wip">
              <span className="fin-th">Project</span>
              <span className="fin-th pbill-right">Contract</span>
              <span className="fin-th pbill-right">Est. cost</span>
              <span className="fin-th pbill-right">Cost to date</span>
              <span className="fin-th pbill-right">% complete</span>
              <span className="fin-th pbill-right">Earned</span>
              <span className="fin-th pbill-right">Billed</span>
              <span className="fin-th pbill-right">Over/(under)</span>
              <span className="fin-th">Next installment</span>
            </div>
            {data.wip.length === 0 ? (
              <div className="fin-table-empty">No open jobs.</div>
            ) : (
              wipDisplay.listed.map(p => (
                <div
                  key={p.dealId}
                  className="fin-table-row pbill-grid pbill-grid-wip"
                  role="button"
                  tabIndex={0}
                  onClick={() => openProjectPL(p)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openProjectPL(p);
                    }
                  }}
                >
                  <span className="fin-cell">
                    <span className="fin-cell-primary">{p.label}</span>
                    {p.clientName && <div className="fin-cell-sub">{p.clientName}</div>}
                  </span>
                  <ContractCell p={p} />
                  <span className="fin-cell fin-cell-money">{p.estimatedCost > 0 ? fmtMoney(p.estimatedCost) : <span className="fin-cell-muted">no budget</span>}</span>
                  <span className="fin-cell fin-cell-money">{fmtMoney(p.costToDate)}</span>
                  <span className="fin-cell fin-cell-money">
                    <span className="pbill-pct">
                      <span className="pbill-pct-bar"><span style={{ width: `${Math.round((p.percentComplete ?? 0) * 100)}%` }} /></span>
                      {fmtPct(p.percentComplete)}
                    </span>
                  </span>
                  <span className="fin-cell fin-cell-money">{p.percentComplete == null ? '—' : fmtMoney(p.earnedRevenue)}</span>
                  <span className="fin-cell fin-cell-money">{fmtMoney(p.billedToDate)}</span>
                  <span className={`fin-cell fin-cell-money ${p.netOverUnder > 0 ? 'is-over' : p.netOverUnder < 0 ? 'is-under' : ''}`}>
                    {p.percentComplete == null ? '—' : fmtOverUnder(p.netOverUnder)}
                  </span>
                  <span className="fin-cell">
                    {p.billingStyle === 'progress' ? (
                      p.nextInstallment ? (
                        <span className="fin-pill">{PROGRESS_INSTALLMENT_LABELS[p.nextInstallment]}</span>
                      ) : (
                        <span className="fin-pill fin-pill-status" data-status="filled">Fully billed</span>
                      )
                    ) : p.billingStyle === 'full' ? (
                      <span className="fin-pill">Lump sum</span>
                    ) : (
                      <span className="fin-cell-muted">—</span>
                    )}
                  </span>
                </div>
              ))
            )}
            {wipSmall && <WipRollupRow rollup={wipSmall} />}
            {data.wip.length > 0 && (
              <div className="fin-table-row pbill-grid pbill-grid-wip pbill-total-row">
                <span className="fin-cell fin-cell-primary">Total · {data.wip.length} job{data.wip.length === 1 ? '' : 's'}</span>
                <span className="fin-cell fin-cell-money">{fmtMoney(totals.contractValueInProgress)}</span>
                <span className="fin-cell fin-cell-money">{fmtMoney(data.wip.reduce((s, p) => s + p.estimatedCost, 0))}</span>
                <span className="fin-cell fin-cell-money">{fmtMoney(totals.costToDate)}</span>
                <span className="fin-cell fin-cell-money">
                  {totals.contractValueInProgress > 0 ? fmtPct(totals.earnedRevenue / totals.contractValueInProgress) : '—'}
                </span>
                <span className="fin-cell fin-cell-money">{fmtMoney(totals.earnedRevenue)}</span>
                <span className="fin-cell fin-cell-money">{fmtMoney(totals.billedToDate)}</span>
                <span className={`fin-cell fin-cell-money ${netClass}`}>{fmtOverUnder(totals.netOverUnder)}</span>
                <span className="fin-cell" />
              </div>
            )}
          </section>

          <section className="fin-table-wrap pbill-table-wrap">
            <div className="pbill-table-title">
              <h3>Completed projects</h3>
              <span className="pbill-card-hint">Largest contract first · jobs under $10,000 are combined.</span>
            </div>
            <div className="fin-table-header pbill-grid pbill-grid-done">
              <span className="fin-th">Project</span>
              <span className="fin-th">Completed</span>
              <span className="fin-th pbill-right">Contract</span>
              <span className="fin-th pbill-right">Final cost</span>
              <span className="fin-th pbill-right">GP %</span>
              <span className="fin-th pbill-right">Billed</span>
              <span className="fin-th pbill-right">Billed vs contract</span>
            </div>
            {data.completed.length === 0 ? (
              <div className="fin-table-empty">No finalized projects yet.</div>
            ) : (
              completedDisplay.listed.map(p => {
                const gap = p.billedToDate - p.contractValue;
                return (
                  <div
                    key={p.dealId}
                    className="fin-table-row pbill-grid pbill-grid-done"
                    role="button"
                    tabIndex={0}
                    onClick={() => openProjectPL(p)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openProjectPL(p);
                      }
                    }}
                  >
                    <span className="fin-cell">
                      <span className="fin-cell-primary">{p.label}</span>
                      {p.clientName && <div className="fin-cell-sub">{p.clientName}</div>}
                    </span>
                    <span className="fin-cell">{fmtDate(p.completedAt)}</span>
                    <ContractCell p={p} />
                    <span className="fin-cell fin-cell-money">{fmtMoney(p.costToDate)}</span>
                    <span className={`fin-cell fin-cell-money ${p.estimatedGrossProfit < 0 ? 'is-over' : ''}`}>{fmtPct(p.estimatedGrossProfitPct)}</span>
                    <span className="fin-cell fin-cell-money">{fmtMoney(p.billedToDate)}</span>
                    <span className="fin-cell fin-cell-money">
                      {Math.abs(p.contractValue) < 1 && Math.abs(p.billedToDate) < 1 ? (
                        <span className="fin-cell-muted">—</span>
                      ) : Math.abs(gap) < 1 ? (
                        <span className="fin-pill fin-pill-status" data-status="filled">Fully billed</span>
                      ) : (
                        fmtOverUnder(gap)
                      )}
                    </span>
                  </div>
                );
              })
            )}
            {completedSmall && <CompletedRollupRow rollup={completedSmall} />}
          </section>
          {hasCollected && <div className="pbill-footnote">{PROGRESS_BILLING_COLLECTED_NOTE}</div>}
        </>
      )}

      <section className="pbill-card pbill-reports">
        <header className="pbill-card-head">
          <h3><Download size={14} /> Reports</h3>
          <span className="pbill-card-hint">As of {fmtDate(asOf)} · CSV for spreadsheets, PDF for the file.</span>
        </header>
        <div className="pbill-report-grid">
          {REPORT_KINDS.map(kind => (
            <div key={kind} className="pbill-report">
              <div className="pbill-report-copy">
                <strong>{PROGRESS_BILLING_REPORT_LABELS[kind]}</strong>
                <span>{REPORT_HINTS[kind]}</span>
              </div>
              <div className="pbill-report-actions">
                <button
                  type="button"
                  className="fin-secondary-btn"
                  disabled={!data.ready || exporting != null}
                  onClick={() => runExport(kind, 'csv')}
                >
                  {exporting === `${kind}:csv` ? <Loader2 size={13} className="pbill-spin" /> : <FileSpreadsheet size={13} />} CSV
                </button>
                <button
                  type="button"
                  className="fin-secondary-btn"
                  disabled={!data.ready || exporting != null}
                  onClick={() => runExport(kind, 'pdf')}
                >
                  {exporting === `${kind}:pdf` ? <Loader2 size={13} className="pbill-spin" /> : <FileText size={13} />} PDF
                </button>
              </div>
            </div>
          ))}
        </div>
        {exportError && <div className="pbill-export-error">{exportError}</div>}
      </section>
    </div>
  );
};

export default ProgressBillingSection;
